// Interactive board engine for the Lobby tangram.
// Base geometry (TANGRAM_BASE_V1) is never modified — pieces only receive a
// per-user transform {dx, dy, rot} (rot in 45° steps around the piece center).
import { TANGRAM_BASE_V1 } from "./tangram";

export const PIECE_IDS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7"];

export const BASE_PTS = Object.fromEntries(
  PIECE_IDS.map((id) => [id, TANGRAM_BASE_V1[id].split(" ").map((p) => p.split(",").map(Number))])
);

// Rotation centers = vertex average of each base polygon.
export const ROT_CENTERS = Object.fromEntries(
  PIECE_IDS.map((id) => {
    const pts = BASE_PTS[id];
    const s = pts.reduce((a, p) => [a[0] + p[0], a[1] + p[1]], [0, 0]);
    return [id, { x: s[0] / pts.length, y: s[1] / pts.length }];
  })
);

export const ZERO_LAYOUT = Object.fromEntries(PIECE_IDS.map((id) => [id, { dx: 0, dy: 0, rot: 0 }]));

export function normalizeLayout(raw) {
  const out = {};
  for (const id of PIECE_IDS) {
    const t = raw?.[id];
    out[id] = t && Number.isFinite(t.dx) && Number.isFinite(t.dy) && Number.isFinite(t.rot)
      ? { dx: t.dx, dy: t.dy, rot: t.rot }
      : { dx: 0, dy: 0, rot: 0 };
  }
  return out;
}

export function rotatePoint(p, c, deg) {
  if (!deg) return [p[0], p[1]];
  const r = (deg * Math.PI) / 180, cos = Math.cos(r), sin = Math.sin(r);
  const x = p[0] - c.x, y = p[1] - c.y;
  return [c.x + x * cos - y * sin, c.y + x * sin + y * cos];
}

export function transformPts(id, t) {
  const c = ROT_CENTERS[id];
  return BASE_PTS[id].map((p) => {
    const q = rotatePoint(p, c, t.rot || 0);
    return [q[0] + (t.dx || 0), q[1] + (t.dy || 0)];
  });
}

// ---------- geometry helpers ----------
function ptSegClosest(p, a, b) {
  const abx = b[0] - a[0], aby = b[1] - a[1];
  const l2 = abx * abx + aby * aby;
  let u = l2 ? ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / l2 : 0;
  u = Math.max(0, Math.min(1, u));
  const q = [a[0] + u * abx, a[1] + u * aby];
  return { pt: q, d: Math.hypot(p[0] - q[0], p[1] - q[1]) };
}

function orient(a, b, c) {
  const v = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  return v > 1e-9 ? 1 : v < -1e-9 ? -1 : 0;
}

function segsIntersect(a, b, c, d) {
  const o1 = orient(a, b, c), o2 = orient(a, b, d), o3 = orient(c, d, a), o4 = orient(c, d, b);
  return o1 !== o2 && o3 !== o4 && o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0;
}

function pointInPoly(p, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}

function polysIntersect(A, B) {
  for (let i = 0; i < A.length; i++) {
    const a1 = A[i], a2 = A[(i + 1) % A.length];
    for (let j = 0; j < B.length; j++) {
      if (segsIntersect(a1, a2, B[j], B[(j + 1) % B.length])) return true;
    }
  }
  return pointInPoly(A[0], B) || pointInPoly(B[0], A);
}

const TOUCH_EPS = 3;      // considered "glued" if boundary gap ≤ this
const VERTEX_SNAP = 60;   // magnet radius for vertex-to-vertex snapping

// Snap the dropped piece so it is always attached (vertex or edge) to at
// least one other piece. Returns the adjusted transform.
export function resolveDrop(id, t, layout) {
  const myPts = transformPts(id, t);
  const others = PIECE_IDS.filter((o) => o !== id).map((o) => transformPts(o, layout[o]));

  for (const O of others) if (polysIntersect(myPts, O)) return t;

  // Closest boundary pair (vertex-to-edge, both directions) across all pieces.
  let bBest = { d: Infinity, delta: [0, 0] };
  // Closest vertex-to-vertex pair.
  let vBest = { d: Infinity, delta: [0, 0] };

  for (const O of others) {
    for (const v of myPts) {
      for (const w of O) {
        const d = Math.hypot(w[0] - v[0], w[1] - v[1]);
        if (d < vBest.d) vBest = { d, delta: [w[0] - v[0], w[1] - v[1]] };
      }
      for (let j = 0; j < O.length; j++) {
        const { pt, d } = ptSegClosest(v, O[j], O[(j + 1) % O.length]);
        if (d < bBest.d) bBest = { d, delta: [pt[0] - v[0], pt[1] - v[1]] };
      }
    }
    for (const w of O) {
      for (let i = 0; i < myPts.length; i++) {
        const { pt, d } = ptSegClosest(w, myPts[i], myPts[(i + 1) % myPts.length]);
        if (d < bBest.d) bBest = { d, delta: [w[0] - pt[0], w[1] - pt[1]] };
      }
    }
  }

  if (bBest.d <= TOUCH_EPS) return t;                       // already glued
  const pick = vBest.d <= VERTEX_SNAP ? vBest : bBest;      // magnet to vertex, else pull to contact
  return { ...t, dx: t.dx + pick.delta[0], dy: t.dy + pick.delta[1] };
}

// Visible board area: union of the original square and every piece's bbox.
// Grows only when pieces extend beyond the original square.
export function boardBBox(layout) {
  let minX = 0, minY = 0, maxX = 1000, maxY = 1000;
  for (const id of PIECE_IDS) {
    for (const [x, y] of transformPts(id, layout[id])) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
  }
  return { x: Math.floor(minX), y: Math.floor(minY), w: Math.ceil(maxX) - Math.floor(minX), h: Math.ceil(maxY) - Math.floor(minY) };
}

// Clean SVG with ONLY the pieces and their colors (transparent background).
export function buildSnapshotSvg(layout, colors) {
  const shapes = PIECE_IDS.map((id) => ({ id, pts: transformPts(id, layout[id]) }));
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const { pts } of shapes) for (const [x, y] of pts) {
    minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
  }
  const pad = 40;
  const w = maxX - minX + pad * 2, h = maxY - minY + pad * 2;
  const polys = shapes
    .map(({ id, pts }) => `<polygon points="${pts.map((p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(" ")}" fill="${colors[id]}" stroke="#FFFFFF" stroke-width="14" stroke-linejoin="miter"/>`)
    .join("");
  return { svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${(minX - pad).toFixed(2)} ${(minY - pad).toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)}">${polys}</svg>`, w, h };
}
